'use client';

import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { FIRST_TAB_SKELETON_MS, TabViewCache } from '@/lib/tab-view-cache';
import { getMiniAppSection } from '@/lib/page-data-policy';
import type { MiniAppPageProps } from './miniapp-home-client';

const CacheContext = createContext<TabViewCache<MiniAppPageProps> | null>(null);

export function MiniAppTabCacheProvider({ children }: { children: ReactNode }) {
  const [cache] = useState(() => new TabViewCache<MiniAppPageProps>());
  return <CacheContext.Provider value={cache}>{children}</CacheContext.Provider>;
}

export function useMiniAppTabData(serverProps: MiniAppPageProps, path: string) {
  const cache = useContext(CacheContext);
  if (!cache) throw new Error('MiniApp 分頁暫存尚未初始化');
  useSyncExternalStore(cache.subscribe, cache.snapshot, cache.serverSnapshot);
  const scope = JSON.stringify([serverProps.storeId, serverProps.currentUserId, serverProps.isTeacher, serverProps.devPreviewEnabled]);
  const revision = serverProps.dataRevision;
  const section = getMiniAppSection(path, serverProps.isTeacher);
  const serverSection = getMiniAppSection(serverProps.initialPath, serverProps.isTeacher);
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(mountedAt);
  const [animatedPath, setAnimatedPath] = useState<string | null>(null);
  const matches = cache.scope === scope && cache.revision === revision;
  const data = section === serverSection ? serverProps : matches ? cache.pages.get(section) : undefined;
  const visit = cache.scope === scope ? cache.visits.get(section) : undefined;
  const readyAt = visit?.readyAt ?? mountedAt + FIRST_TAB_SKELETON_MS;
  const ready = Boolean(data) && (Boolean(visit?.shown) || now >= readyAt);

  useEffect(() => {
    cache.receive(scope, revision, serverSection, serverProps);
  }, [cache, scope, revision, serverSection, serverProps]);

  useEffect(() => {
    cache.start(scope, revision, section, Date.now());
    const deadline = cache.visits.get(section)!.readyAt;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, deadline - Date.now()));
    return () => window.clearTimeout(timer);
  }, [cache, scope, revision, section]);

  useEffect(() => {
    if (ready) {
      cache.show(section);
    }
  }, [cache, section, ready, path]);

  const beginVisit = useCallback((target: string) => {
    const targetSection = getMiniAppSection(target, serverProps.isTeacher);
    // 與切換路徑在同一次事件更新，避免先顯示內容再從透明重播。
    setAnimatedPath(cache.visits.get(targetSection)?.shown ? target : null);
    cache.start(scope, revision, targetSection, Date.now());
  }, [cache, scope, revision, serverProps.isTeacher]);

  const hasCachedPage = useCallback((target: string) => {
    return cache.scope === scope && cache.revision === revision
      && cache.pages.has(getMiniAppSection(target, serverProps.isTeacher));
  }, [cache, scope, revision, serverProps.isTeacher]);

  return {
    hasCachedPage,
    data: data ?? serverProps,
    hasData: Boolean(data),
    ready,
    firstReveal: !visit?.shown,
    animateReturn: animatedPath === path,
    beginVisit,
  };
}
